import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export type Theme = "dark" | "light";

/** `null` is the Factorium palette from the ported handoff tokens — the default. */
export type Palette = string | null;

const STORAGE_KEY = "factorium.theme";
const PALETTE_KEY = "factorium.palette";

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggle: () => void;
  palette: Palette;
  setPalette: (palette: Palette) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * UI-17 — the entire theme is one `light` class on the document root.
 *
 * `tokens/colors.css` defines the dark palette on `:root` and overrides it under
 * `html.light`, so flipping the class re-resolves every custom property and the whole
 * interface follows. **No component may branch on the theme**: the moment one does,
 * that component stops being themeable by the tokens and every later screen has to
 * remember to do the same thing. A test enforces this rather than trusting to
 * discipline — see `theme-discipline.test.ts`.
 *
 * The only two things allowed to know the current theme are this provider and the
 * control that toggles it.
 */
export function ThemeProvider({
  children,
  onPersist,
}: {
  children: React.ReactNode;
  /** Writes the choice back to user_settings, so it follows the user between devices. */
  onPersist?: (theme: Theme) => void;
}) {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);
  const [palette, setPaletteState] = useState<Palette>(readStoredPalette);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    applyPalette(palette);
  }, [palette]);

  const setTheme = useCallback(
    (next: Theme) => {
      // The side effects live outside the state updater on purpose: React may run an
      // updater more than once (StrictMode does, every time), and a network PATCH is
      // not something to fire twice per toggle.
      if (next === theme) return;

      // Written locally first so a reload paints the right theme immediately,
      // before any network call has had a chance to answer.
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // Private browsing, or storage disabled. The theme still applies for
        // this session; it simply will not be remembered.
      }

      setThemeState(next);
      onPersist?.(next);
    },
    [onPersist, theme],
  );

  const setPalette = useCallback((next: Palette) => {
    setPaletteState(next);

    try {
      if (next) window.localStorage.setItem(PALETTE_KEY, next);
      else window.localStorage.removeItem(PALETTE_KEY);
    } catch {
      // Storage disabled. The palette still applies for this session.
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      setTheme,
      toggle: () => setTheme(theme === "dark" ? "light" : "dark"),
      palette,
      setPalette,
    }),
    [theme, setTheme, palette, setPalette],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used inside a ThemeProvider.");
  return context;
}

/** Dark is the default because the tokens define it on bare `:root`. */
export function readStoredTheme(): Theme {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "light" ? "light" : "dark";
  } catch {
    return "dark";
  }
}

export function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle("light", theme === "light");
  // Keeps form controls, scrollbars and the browser's own chrome in step.
  document.documentElement.style.colorScheme = theme;
}

export function readStoredPalette(): Palette {
  try {
    return window.localStorage.getItem(PALETTE_KEY) || null;
  } catch {
    return null;
  }
}

/**
 * The palette is a second flag beside the theme, and works the same way: a
 * `data-palette` attribute on the document root, which the generated
 * `themes/palettes.css` uses to outrank the handoff tokens. Removing the attribute
 * restores the Factorium palette, which is why the default is an absent attribute
 * rather than a named one.
 */
export function applyPalette(palette: Palette): void {
  if (palette) document.documentElement.setAttribute("data-palette", palette);
  else document.documentElement.removeAttribute("data-palette");
}
